// const plansMock = ['PRO_3M', 'PRO_6M', 'PRO_12M', 'FREE_UNLIMITED']

import axios from 'axios'
import { jwtDecode, JwtPayload } from 'jwt-decode'

import { tracerError } from '../../sideEffects.container'

import { billingEngineApi } from './billing-engine-api'
import { OperationType } from './operations'

export type AccountResponse = {
  canCreateNewClientRequest: boolean
  personalDiscountIsAvailable: boolean
  plan: {
    code: string
    shortName: string
    name: string
    monthlyDiscountedPrice: number
    basePrice: number
    discountPercent: number
    totalPrice: number
    isBase: boolean
    isUnlimited: boolean
    isHotOffer: boolean
    conditions: Array<string>
  }
  subscription: {
    subscriptionId: string
    startDate: string
    endDate: string
    status: 'PENDING' | 'ACTIVE' | 'INACTIVE' | 'CANCELLED'
    billingAccountId: string
  }
  client: {
    inn: string
    kpp: string
    shortName: string
    paymentAvailable: boolean
  }
  contract: {
    number: `${string}-${string}`
  }
}

export type HistoryResponse = {
  historyItems: Array<{
    subscriptionRequestId: string
    name: string
    dateStart: string
    status: string
  }>
}

export type ProjectSettingsResponse = {
  isProjectLimitationEnabled: boolean
  shouldShowTariffMenu: boolean
}

export type AvailableLimitResponse = {
  availableLimit: number
}

export type OperationId =
  | 'CREATE_STAMP'
  | 'SEND_DOC_TO_CHECK'
  | 'BATCH_UPLOAD_DOCS_TO_DOCUMENTARY'

export const billingEngineQueries = {
  getProjectSettings: ({ projectId }: { projectId?: string }) => ({
    queryKey: ['billing-engine', 'project-settings', projectId],
    queryFn: () =>
      billingEngineApi
        .get<ProjectSettingsResponse>('/project-setting', {
          params: { projectId },
        })
        .then(r => r.data),
  }),

  getAvailableLimit: ({
    projectId,
    limitationOperationCode,
  }: {
    projectId: string
    limitationOperationCode: OperationId
  }) => ({
    queryKey: [
      'billing-engine',
      'available-limit',
      projectId,
      limitationOperationCode,
    ],
    queryFn: () =>
      billingEngineApi
        .get<AvailableLimitResponse>(
          `subscription/limit/available/${limitationOperationCode}`,
          { params: { projectId } },
        )
        .then(r => r.data),
  }),
  getBillingHistory: () => ({
    queryKey: ['billing-engine', 'history'],
    queryFn: () =>
      billingEngineApi
        .get<HistoryResponse>('subscription/history')
        .then(r => r.data),
  }),
  getBillingAccount: () => ({
    queryKey: ['billing-engine', 'account'],
    queryFn: () =>
      billingEngineApi
        .post<AccountResponse>('/accounts/details')
        .catch(e => {
          if (axios.isAxiosError(e)) {
            try {
              const jwtDecoded = jwtDecode<
                JwtPayload & { userId: string; inn: string; kpp: string }
              >(e.config.headers['Authorization'])

              if (jwtDecoded.userId) {
                tracerError.error(e.config, {
                  component: 'billingEngineQueries',
                  userId: jwtDecoded.userId,
                  keys: {
                    inn: jwtDecoded.inn,
                    kpp: jwtDecoded.kpp,
                  },
                })
              } else {
                tracerError.error(e.config, {
                  component: 'billingEngineQueries',
                })
              }
            } catch {
              tracerError.error(e, { component: 'billingEngineQueries' })
            }
          } else {
            tracerError.error(e, { component: 'billingEngineQueries' })
          }
          throw e
        })
        .then(r => r.data),
  }),
}

export type GroupedOperation = {
  operationGroupName: string
  operationObjectId: string
}

interface CheckOperationRequest {
  operationId: OperationId | OperationType
  body: {
    projectId: string
    operationCount: number
    groupedOperations?: GroupedOperation[]
  }
}

export interface CheckOperationResponse {
  isAllowed: boolean
  requestedLimit: number
  availableLimit: number
}

interface CommitOperationRequest {
  operationId: OperationId
  operationCount: number
  projectId: string
  groupedOperations?: GroupedOperation[]
}

export const billingEngineMutations = {
  checkOperation: () => ({
    mutationFn: ({ operationId, body }: CheckOperationRequest) =>
      billingEngineApi
        .post<CheckOperationResponse>(
          `subscription/limit/check-operation/${operationId}`,
          body,
        )
        .then(r => r.data),
  }),
  commitOperation: () => ({
    mutationFn: ({
      operationId,
      operationCount,
      projectId,
      groupedOperations,
    }: CommitOperationRequest) =>
      billingEngineApi
        .post<void>(`/subscription/limit/commit-operation/${operationId}`, {
          operationCount,
          projectId,
          groupedOperations,
        })
        .then(r => r.data),
  }),
}
